import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Loader2, Zap, Upload, X, CheckCircle, AlertCircle, ImagePlus } from 'lucide-react';
import { api } from '../lib/api';

interface TemplateBrowserProps {
  onSelectTemplate: (template: any) => void;
}

interface UploadProgress {
  fileName: string;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
}

export function TemplateBrowser({ onSelectTemplate }: TemplateBrowserProps) {
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  useEffect(() => {
    loadTemplates();
  }, [selectedCategory]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      const data = await api.getTemplates(params);
      setTemplates(data.templates);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const processFiles = useCallback(async (files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter(file =>
      file.type.startsWith('image/')
    );

    if (imageFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(imageFiles.map(f => ({
      fileName: f.name,
      status: 'pending'
    })));

    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];

      setUploadProgress(prev => prev.map((p, idx) =>
        idx === i ? { ...p, status: 'uploading' } : p
      ));

      try {
        const { dataUrl, width, height } = await readImageFile(file);
        const name = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');

        await api.uploadTemplate({
          name,
          image_data: dataUrl,
          width,
          height,
        });

        setUploadProgress(prev => prev.map((p, idx) =>
          idx === i ? { ...p, status: 'complete' } : p
        ));
      } catch (error) {
        setUploadProgress(prev => prev.map((p, idx) =>
          idx === i ? { ...p, status: 'error', error: String(error) } : p
        ));
      }
    }

    // Reload templates after all uploads complete
    await loadTemplates();

    // Clear progress after a delay
    setTimeout(() => {
      setUploadProgress([]);
      setIsUploading(false);
    }, 3000);
  }, []);

  const readImageFile = (file: File): Promise<{ dataUrl: string; width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          resolve({
            dataUrl: e.target?.result as string,
            width: img.width,
            height: img.height,
          });
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [processFiles]);

  const filteredTemplates = templates.filter((template) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      template.name.toLowerCase().includes(query) ||
      template.tags.some((tag: string) => tag.toLowerCase().includes(query))
    );
  });

  const categories = [
    { value: 'all', label: 'All' },
    { value: 'classic', label: 'Classic' },
    { value: 'trending', label: 'Trending' },
    { value: 'custom', label: 'Custom' },
  ];

  return (
    <div
      className="space-y-8 animate-fade-in relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Hidden file input for click-to-upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-primary-500/10 backdrop-blur-sm border-4 border-dashed border-primary-500 rounded-3xl flex items-center justify-center">
          <div className="text-center p-8 bg-white/90 rounded-2xl shadow-2xl">
            <Upload className="w-16 h-16 text-primary-500 mx-auto mb-4 animate-bounce" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Drop Images Here</h3>
            <p className="text-gray-600">Release to add as templates</p>
          </div>
        </div>
      )}

      {/* Upload progress panel */}
      {uploadProgress.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 bg-white rounded-2xl shadow-2xl p-4 w-80 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-gray-900">Uploading Templates</h4>
            {!isUploading && (
              <button
                onClick={() => setUploadProgress([])}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {uploadProgress.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                {item.status === 'pending' && (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
                {item.status === 'uploading' && (
                  <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
                )}
                {item.status === 'complete' && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
                {item.status === 'error' && (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                )}
                <span className={`truncate flex-1 ${item.status === 'error' ? 'text-red-600' : 'text-gray-700'}`}>
                  {item.fileName}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center space-y-3">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-primary-600 to-gray-900 bg-clip-text text-transparent">
          Choose Your Template
        </h2>
        <p className="text-gray-600 text-lg">Select a meme template to get started</p>
      </div>

      {/* Bulk upload drop zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed border-gray-300 hover:border-primary-400 rounded-2xl p-6 text-center cursor-pointer transition-all duration-200 hover:bg-primary-50/50 group"
      >
        <div className="flex items-center justify-center gap-4">
          <div className="p-3 bg-primary-100 rounded-xl group-hover:bg-primary-200 transition-colors">
            <ImagePlus className="w-8 h-8 text-primary-600" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-gray-900 group-hover:text-primary-700 transition-colors">
              Add Your Own Templates
            </h3>
            <p className="text-sm text-gray-500">
              Drag & drop images here or click to browse. Add multiple at once!
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors" size={20} />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-12 text-base"
          />
        </div>

        <div className="flex gap-2 bg-white/80 backdrop-blur-sm p-1.5 rounded-2xl shadow-sm">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`
                px-5 py-2.5 rounded-xl font-semibold transition-all duration-200 whitespace-nowrap
                ${selectedCategory === category.value
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30 scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-32">
          <Loader2 className="animate-spin text-primary-500 mb-4" size={48} />
          <p className="text-gray-500 font-medium">Loading templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-32">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
            <Search className="text-gray-400" size={32} />
          </div>
          <p className="text-gray-500 text-lg font-medium">No templates found</p>
          <p className="text-gray-400 text-sm mt-2">Try a different search or category</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {filteredTemplates.map((template, index) => (
            <button
              key={template.id}
              onClick={() => onSelectTemplate(template)}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 animate-scale-in"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-50 relative overflow-hidden">
                <img
                  src={template.image_url}
                  alt={template.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center text-gray-400 font-medium">
                  {template.name}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                    <Zap className="text-primary-500" size={16} />
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2 line-clamp-1 group-hover:text-primary-600 transition-colors">
                  {template.name}
                </h3>
                {template.tags && template.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {template.tags.slice(0, 2).map((tag: string) => (
                      <span
                        key={tag}
                        className="text-xs bg-primary-50 text-primary-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
